/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp.  2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'./FilterDockController',
	'jquery',
	'underscore',
	'../utils/Dragger',
	'../../widgets/livewidget/nls/StringResources'
], (View, FilterDockController, $, _, Dragger, StringResources) => {

	const FilterContainerView = View.extend({

		className: 'filterDockContainer',
		tabDock: null,
		globalDock: null,

		init(options) {
			FilterContainerView.inherited('init', this, arguments);
			this.dashboard = options.dashboardApi;
			this.viewModel = {};
			this.filterControllers = [];
			/**
			 * currently filter container view supports the following extraOptions
			 *  extraClassName: 'something'
				enableAnimation: true/false, default to true
				showGlobalDock: true/false, default to true
				excludeAddToCanvas: true/false default to false
			 */
			this.extraOptions = options.extraOptions;
			// Note: In order for a child perspective of dashboard-core to overwrite the values of translateable strings
			// there is the StringResources service that is created/registered in dashboard-core. The number given when registering a
			// resource is the priority. The default is 100 which is what dashboard-core is registered at.
			// We register dashboard-livewidget at priority 90. Storytelling, for example, is priority 1 as no other component
			// should be able to overwrite its strings (nor would they be needed to)
			// We are doing this because the strings for the filter dock reference "tabs" when in Storytelling there are "scenes" instead
			// so the strings (using the same keys) exist in Storytelling but use more Storytelling correct wording.
			this.stringResources = this.dashboard.getDashboardCoreSvc('.StringResources');
			this.stringResources.register(StringResources, 90);

			// the filter dock is not active by default
			this._isActive = false;
		},

		getApi() {
			return {
				isGlobalDockShown: (this.extraOptions && this.extraOptions.showGlobalDock === false) ? false : true,
				currentScope: () => {
					return this.tabDock.scope;
				},
				activate: this.activate.bind(this),
				deactivate: this.deactivate.bind(this),
				isActive: this.isActive.bind(this),
				switchDatasource: this.switchDatasource.bind(this)
			};
		},

		_setupEvents() {
			// change scope every time a tab changes
			if (this.tabDock) {
				this.dashboard.on('tab:tabChanged', this.onScopeChanged, this);
			}

			this.dashboard.on('filterDock:resized', this._resize, this);

			this.on('resize', this._resize, this);

			// trigger resize events when the filter dock is finished toggling
			this.$el.on('shown.bs.collapse hidden.bs.collapse', this._resizeWindow.bind(this));
		},

		_teardownEvents() {
			// change scope every time a tab changes
			this.dashboard.off('tab:tabChanged', this.onScopeChanged, this);

			this.dashboard.off('filterDock:resized', this._resize, this);

			this.off('resize', this._resize, this);

			// trigger resize events when the filter dock is finished toggling
			this.$el.off('shown.bs.collapse hidden.bs.collapse', this._resizeWindow.bind(this));
		},

		_createNewFilterDocks(details, pageContextAPI, synchronizeData) {
			const filterControllers = [];
			const singleDockMode = !details.showTabDock;
			if (details.showGlobalDock) {
				filterControllers.push(this._createGlobalDock(singleDockMode, pageContextAPI, synchronizeData));
			}
			if (details.showTabDock) {
				filterControllers.push(this._createTabDock(details.currentScopeId, pageContextAPI, synchronizeData));
			}
			return filterControllers;
		},

		_createGlobalDock(singleDockMode, pageContextAPI, synchronizeData) {
			const _authorHint = singleDockMode ? this.stringResources.get('filter_drag_and_drop_single_dock') : this.stringResources.get('filter_drag_and_drop_all_tabs');
			const options = this._getCommonOptions(pageContextAPI, synchronizeData);
			options.singleDockMode = singleDockMode;
			options.authorHint = _authorHint;
			options.consumeHint = this.stringResources.get('filter_empty_consume_mode');
			this.globalDock = new FilterDockController(options, 'global');
			return this.globalDock;
		},

		_createTabDock(currentScopeId, pageContextAPI, synchronizeData) {
			const options = this._getCommonOptions(pageContextAPI, synchronizeData);

			options.authorHint = this.stringResources.get('filter_drag_and_drop_current_tab');
			options.consumeHint = this.stringResources.get('filter_empty_consume_mode');
			options.excludeAddToCanvas = this.extraOptions && this.extraOptions.excludeAddToCanvas === true;

			this.tabDock = new FilterDockController(options, currentScopeId);
			return this.tabDock;
		},

		_getCommonOptions(pageContextAPI, synchronizeData) {
			return {
				parentEl: this.$el,
				pageContextAPI: pageContextAPI,
				dashboardApi: this.dashboard,
				viewModel: this.viewModel,
				handlers: {
					onClickFilterDockIcon: this.onClickFilterDockIcon.bind(this),
					onAddRemoveFilterItem: synchronizeData.onAddRemoveFilterItem.bind(synchronizeData)
				}
			};
		},

		_insertDragger() {
			this.dragger = new Dragger({
				parent: this,
				prevSelector: '.global.filterDock',
				nextSelector: '.other.filterDock',
				minPercent: 6
			});
		},

		_resize() {
			_.each(this.filterControllers, controller => controller.resize());
		},

		_resizeWindow() {
			$(window).resize();
		},

		_getBoardDetails: function () {

			var boardModel =  this.dashboard.getFeature('internal').getBoardModel();

			/*
				* There are a number of different layout types for dashboards/stories.
				* The single page dashboards ('absolute' and 'container') and infographics ('singlePage') do not show the tab dock
				* The 'tab dock' (local filters) is for tabbed dashboards and stories only, so the rest of the types.
				*/
			let showTabDock = true;
			if (boardModel.layout) {
				switch (boardModel.layout.type) {
					// 'singlePage' is an infographic dashboard
					case 'singlePage':
						// 'absolute' is a freeform single page dashboard
						showTabDock = false;
						break;
					case 'absolute':
						// 'container' is a single page dashboard with a template
						showTabDock = false;
						break;
					case 'container':
						showTabDock = false;
						break;
				}
			}

			const showGlobalDock = !(this.extraOptions && this.extraOptions.showGlobalDock === false);

			let currentScopeId;
			if (showTabDock && boardModel.layout && boardModel.layout.items.length > 0 && boardModel.layout.items[0] && boardModel.layout.items[0].id) {
				const canvas = this.dashboard.getFeature('Canvas');
				const selectedPage = canvas.getSelectedContentList({ type: 'page' });
				currentScopeId = !_.isEmpty(selectedPage) && selectedPage[0].getId() || boardModel.layout.items[0].id;
			} else {
				currentScopeId = boardModel.layout && boardModel.layout.id;
			}

			return {
				showTabDock: showTabDock,
				showGlobalDock: showGlobalDock,
				currentScopeId: currentScopeId
			};
		},

		/**
		 * Activate filter dock
		 */
		activate() {
			if (this._isActive) {
				// already active, do nothing
				return;
			}
			this._isActive = true;
			this._setupEvents();
		},

		/**
		 * limit tab filters to specific datasource
		 */
		switchDatasource: function(datasourceId, extraOptions) {
			this.tabDock.switchDatasource(datasourceId, extraOptions);
		},

		/**
		 * Deactivate filter dock
		 */
		deactivate() {
			if (!this._isActive) {
				// already deactivated, do nothing
				return;
			}
			this._isActive = false;
			this._teardownEvents();
		},

		/**
		 * Check whether the filter dock is activated or not
		 *
		 * @return {boolean} TRUE if active and FALSE if not
		 */
		isActive() {
			return this._isActive;
		},

		render: function() {
			var isAuthoringMode = this.dashboard.getMode() === 'authoring';
			return Promise.all([

				this._getPageContextAPI(),
				this._getSynchronizeDataService()
			]).then(results => {
				const [pageContextAPI, synchronizeData] = results;
				const details = this._getBoardDetails();
				const promises = [];
				this.filterControllers = this._createNewFilterDocks(details, pageContextAPI, synchronizeData);

				_.each(this.filterControllers, (controller, index) => {
					if (index === 1) {
						this._insertDragger();
					}
					this.$el.append(controller.view.$el);
					promises.push(controller.render(isAuthoringMode));
				});

				this.$el.addClass('collapse');
				this.$el.attr('data-toggle', false);
				this.$el.attr('id', 'filterDockContainer');
				if (this.extraOptions && this.extraOptions.extraClassName) {
					this.$el.addClass(this.extraOptions.extraClassName);
				}
				this.activate();

				return Promise.all(promises);
			});
		},

		changeToAuthorMode() {
			return Promise.resolve().then(() => {
				this.expand();
				_.each(this.filterControllers, controller => controller.changeToAuthorMode());
				this.dashboard.triggerDashboardEvent('filterDock:showItemMenu');
			});
		},

		changeToConsumeMode() {
			return Promise.resolve().then(() => {
				_.each(this.filterControllers, controller => controller.changeToConsumeMode());
				return this._getFilterCount();
			}).then((filterCount) => {
				if (filterCount === 0) {
					this.collapse();
				} else {
					this.dashboard.triggerDashboardEvent('filterDock:hideItemMenu');
				}
			});
		},

		_getPageContextAPI: function () {
			return Promise.resolve(this.dashboard.getFeature('PageContextService').getPageContext());
		},

		_getFilterCount() {
			return this._getPageContextAPI().then((pageContextAPI) => {
				const filterEntries = pageContextAPI.getPageContextItems({
					origin: 'filter',
					_strictMatch: true
				});
				return filterEntries.length;
			});
		},

		_getSynchronizeDataService() {
			return this.dashboard.getDashboardSvc('SynchronizeDataService')
				.then((SynchronizeDataService) => {
					return SynchronizeDataService.getSynchronizeData();
				});
		},

		onScopeChanged(source) {
			this.tabDock.changeScope(source.modelId);
		},

		disable() {
			this.$el.addClass('filter-dock-disabled');
		},

		enable() {
			this.$el.removeClass('filter-dock-disabled');
		},

		expand() {
			if (this._isAnimationEnabled()) {
				this.$el.collapse('show');
			} else {
				this.$el.show();
			}
		},

		collapse() {
			if (this._isAnimationEnabled()) {
				this.$el.collapse('hide');
			} else {
				this.$el.hide();
			}
		},

		_isAnimationEnabled() {
			return !(this.extraOptions && this.extraOptions.enableAnimation === false);
		},

		removeTabDock() {
			if (this.tabDock) {
				this.tabDock.destroy();
				this.tabDock = null;
				this.dashboard.off('tab:tabChanged', this.onScopeChanged, this);
			}
			if (this.globalDock) {
				this.globalDock.view.$el.removeClass('global').addClass('only');
			}
		},

		onClickFilterDockIcon(scope) {
			if (!this.tabDock) {
				return;
			}
			if (this.tabDock.isCollapsed() || this.tabDock.isSizedByDrag()) {
				this.tabDock.resetDockWidth();
			} else if (this.globalDock && (this.globalDock.isCollapsed() || this.globalDock.isSizedByDrag())) {
				this.globalDock.resetDockWidth();
			} else {
				if (scope === 'global' && this.tabDock.toggleCollapse()) {
					this.globalDock && this.globalDock.expandDock();
				} else if (this.globalDock && this.globalDock.toggleCollapse()) {
					this.tabDock.expandDock();
				}
			}
		},

		remove() {
			this.deactivate();
			this.filterControllers.length = 0;
			if (this.tabDock) {
				this.tabDock.destroy();
				this.tabDock = null;
			}
			if (this.globalDock) {
				this.globalDock.destroy();
				this.globalDock = null;
			}
			if (this.dragger) {
				this.dragger.remove();
				this.dragger = null;
			}
			FilterContainerView.inherited('remove', this, arguments);
		}
	});

	return FilterContainerView;
});
